import m from 'mithril';

import { parseReleaseFeed } from '../services/release';
import { runtimeUrls } from '../services/env';
import { captureException } from '../services/sentry';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { Loading } from '../components/Loading';
import { Type } from '../components/Typography';
import downloadImage from '../assets/images/download.svg';
import downloadMobileImage from '../assets/images/download_mobile.svg';

/**
 * Download route attrs.
 */
export interface DownloadAttrs extends m.Attributes {
  /** New user marker segment. */
  newUser?: string;
}

/**
 * App download page.
 */
export const Download: m.ClosureComponent<DownloadAttrs> = () => {
  const state = {
    loading: true,
    downloadURL: '',
    error: '',
  };

  return {
    oninit: () => {
      void parseReleaseFeed()
        .then((url) => {
          state.downloadURL = url;
          state.loading = false;
          m.redraw();
        })
        .catch((error: unknown) => {
          captureException(error);
          console.error('failed to load latest version:', error);
          state.error = `Unable to determine latest download link. Please contact ${runtimeUrls.supportEmail}`;
          state.loading = false;
          m.redraw();
        });
    },
    view: ({ attrs }) => {
      if (state.error) {
        return m('section', { class: 'page page--narrow download-page' }, [
          m(Alert, {
            title: 'Something went wrong!',
            message: state.error,
            variant: 'danger',
          }),
        ]);
      }

      return m('section', { class: 'page download-page' }, [
        m('div', { class: 'download-card' }, [
          m('aside', { class: 'download-card__media download-card__media--desktop' }, [
            m('img', { src: downloadImage, alt: 'Download for Mac' }),
          ]),
          m('aside', { class: 'download-card__media download-card__media--mobile' }, [
            m('img', {
              src: downloadMobileImage,
              alt: 'MeshOS is not yet available for mobile devices',
            }),
          ]),
          m('section', { class: 'download-card__content' }, [
            state.loading ? m(Loading, { large: true }) : [
              attrs.newUser ? [
                m(Type, { size: 'heading' }, 'Account Activated!'),
                m('hr'),
              ] : null,
              m(Type, attrs.newUser
                ? 'Download the MeshOS app to get started.'
                : 'Download the MeshOS app for Mac to begin.'),
              m(Button, {
                href: state.downloadURL,
                download: '',
                full: true,
                size: 'l',
              }, 'Download'),
              m(Type, { size: 'subtext', className: 'download-card__note' }, 'requires macOS 11 or greater'),
            ],
          ]),
        ]),
      ]);
    },
  };
};
